import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
  X, CheckCircle2, AlertTriangle, XCircle, Send, RefreshCw, ShieldCheck,
  Search, Loader2,
} from 'lucide-react';
import {
  preSendCheckRfqCampaign, sendRfqCampaign,
  bulkEnrichSuppliers, bulkVerifySupplierEmails,
  enrichSupplier, verifySupplierEmail,
} from '../../lib/rfqApi';
import { readApiError } from '../../lib/api';

const RESULT_BADGE = {
  ready: 'bg-emerald-400/15 text-emerald-200 border-emerald-400/30',
  needs_attention: 'bg-amber-400/15 text-amber-200 border-amber-400/30',
  blocked: 'bg-red-400/15 text-red-200 border-red-400/30',
};

const EV_LABEL = {
  verified: 'Verified',
  valid_mx: 'MX Valid',
  unverified: 'Unverified',
  invalid: 'Invalid',
  unknown: 'Unknown',
  risky: 'Risky',
  missing: 'Missing',
};

const EV_COLOR = {
  verified: 'text-emerald-300',
  valid_mx: 'text-emerald-300',
  unverified: 'text-white/50',
  invalid: 'text-red-300',
  unknown: 'text-amber-300',
  risky: 'text-amber-300',
  missing: 'text-red-300',
};

const ENRICH_LABEL = {
  enriched: 'Enriched',
  verified: 'Verified',
  needs_enrichment: 'Needs Enrichment',
  invalid: 'Invalid',
  no_contact_found: 'No Contact',
};

const StatCard = ({ tone, value, label, testid }) => {
  const toneClass =
    tone === 'green' ? 'border-emerald-400/30 bg-emerald-400/5'
    : tone === 'amber' ? 'border-amber-400/30 bg-amber-400/5'
    : tone === 'red' ? 'border-red-400/30 bg-red-400/5'
    : 'border-white/10 bg-white/[0.02]';
  const numClass =
    tone === 'green' ? 'text-emerald-300'
    : tone === 'amber' ? 'text-amber-300'
    : tone === 'red' ? 'text-red-300'
    : 'text-white';
  return (
    <div className={`border ${toneClass} px-4 py-3`} data-testid={testid}>
      <div className={`text-2xl font-bold ${numClass}`}>{value}</div>
      <div className="text-[11px] uppercase tracking-wider text-white/60 mt-0.5">{label}</div>
    </div>
  );
};

export const RfqPreSendPanel = ({ campaign, onClose, onSent }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [allowRisky, setAllowRisky] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [showBlocked, setShowBlocked] = useState(false);
  const [filter, setFilter] = useState('all'); // all | ready | needs_attention | blocked
  const [search, setSearch] = useState('');
  const [sending, setSending] = useState(false);
  const [busy, setBusy] = useState(null); // 'enrich-all' | 'verify-all' | `enrich-${id}` | `verify-${id}`

  const fetchCheck = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await preSendCheckRfqCampaign(campaign.id);
      setData(data);
    } catch (e) {
      toast.error(readApiError(e, 'Failed to run pre-send check'));
    } finally {
      setLoading(false);
    }
  }, [campaign.id]);

  useEffect(() => { fetchCheck(); }, [fetchCheck]);

  const counts = data?.counts || {};
  const breakdown = data?.breakdown || [];

  const willSendCount = allowRisky
    ? (counts.would_send_with_risky || 0)
    : (counts.would_send_default || 0);
  const blockedCount = (counts.total || 0) - willSendCount;

  const filteredRows = breakdown.filter((b) => {
    if (filter !== 'all' && b.result !== filter) return false;
    if (search) {
      const s = search.toLowerCase();
      if (!b.company_name.toLowerCase().includes(s)
        && !(b.email || '').toLowerCase().includes(s)) return false;
    }
    return true;
  });

  const blockedRows = breakdown.filter((b) => {
    if (b.result === 'blocked') return true;
    if (!allowRisky && b.result === 'needs_attention') return true;
    return false;
  });

  // Quick actions ----------------------------------------------------------
  const runBulkEnrich = async () => {
    setBusy('enrich-all');
    try {
      const { data } = await bulkEnrichSuppliers();
      if ((data.total || 0) === 0) {
        toast.info('No suppliers need enrichment.');
      } else {
        const remaining = data.remaining || 0;
        const failed = data.failed || 0;
        const parts = [
          `Enriched ${data.enriched || 0}/${data.total || 0}`,
        ];
        if (failed) parts.push(`failed ${failed}`);
        if (remaining) parts.push(`${remaining} pending — run again`);
        toast.success(parts.join(' · '));
      }
      await fetchCheck();
    } catch (e) {
      toast.error(readApiError(e, 'Bulk enrichment failed'));
    } finally { setBusy(null); }
  };

  const runBulkVerify = async () => {
    setBusy('verify-all');
    try {
      const { data } = await bulkVerifySupplierEmails();
      toast.success(`MX verification done: valid ${data.valid_mx || 0}, risky ${data.risky || 0}, invalid ${data.invalid || 0}`);
      await fetchCheck();
    } catch (e) {
      toast.error(readApiError(e, 'Bulk verify failed'));
    } finally { setBusy(null); }
  };

  const runEnrichOne = async (id) => {
    setBusy(`enrich-${id}`);
    try { await enrichSupplier(id); await fetchCheck(); toast.success('Enriched'); }
    catch (e) { toast.error(readApiError(e, 'Enrich failed')); }
    finally { setBusy(null); }
  };

  const runVerifyOne = async (id) => {
    setBusy(`verify-${id}`);
    try { await verifySupplierEmail(id); await fetchCheck(); toast.success('Verified'); }
    catch (e) { toast.error(readApiError(e, 'Verify failed')); }
    finally { setBusy(null); }
  };

  // Send -----------------------------------------------------------------
  const doSend = async () => {
    setSending(true);
    try {
      const { data: result } = await sendRfqCampaign(campaign.id, { allow_risky: allowRisky });
      toast.success(`Sent ${result.sent}/${result.total} (failed: ${result.failed})`);
      setConfirmOpen(false);
      onSent && onSent(result);
    } catch (e) {
      toast.error(readApiError(e, 'Send failed'));
    } finally { setSending(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}
      data-testid="rfq-presend-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-6xl bg-[#0A0A0A] border border-[#00D4FF]/30 max-h-[92vh] overflow-y-auto"
        data-testid="rfq-presend-panel"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 sticky top-0 bg-[#0A0A0A] z-10">
          <div>
            <div className="text-xs uppercase tracking-wider text-[#00D4FF]">Pre-Send Check</div>
            <h2 className="text-xl font-bold">{campaign.campaign_name}</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchCheck}
              disabled={loading}
              data-testid="rfq-presend-refresh"
              className="p-2 text-white/60 hover:text-[#00D4FF] disabled:opacity-50"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={onClose} className="text-white/40 hover:text-white" data-testid="rfq-presend-close">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {loading && !data && (
          <div className="p-16 text-center text-white/50 flex flex-col items-center gap-3">
            <Loader2 className="w-6 h-6 animate-spin text-[#00D4FF]" />
            Running deliverability check…
          </div>
        )}

        {data && (
          <div className="p-6 space-y-6">
            {/* Status cards */}
            <div>
              <div className="text-[11px] uppercase tracking-wider text-white/40 mb-2">
                Deliverability snapshot
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
                <StatCard testid="ps-total" value={counts.total || 0} label="Total Suppliers" tone="neutral" />
                <StatCard testid="ps-ready" value={counts.ready || 0} label="Ready To Send" tone="green" />
                <StatCard testid="ps-verified" value={counts.verified || 0} label="Verified Email" tone="green" />
                <StatCard testid="ps-validmx" value={counts.valid_mx || 0} label="MX Valid" tone="green" />
                <StatCard testid="ps-unverified" value={counts.unverified || 0} label="Unverified" tone="amber" />
                <StatCard testid="ps-risky" value={counts.risky || 0} label="Risky" tone="amber" />
                <StatCard testid="ps-unknown" value={counts.unknown || 0} label="Unknown" tone="amber" />
                <StatCard testid="ps-invalid" value={counts.invalid || 0} label="Invalid" tone="red" />
                <StatCard testid="ps-missing" value={counts.missing_email || 0} label="Missing Email" tone="red" />
                <StatCard testid="ps-needs-enrich" value={counts.needs_enrichment || 0} label="Needs Enrichment" tone="amber" />
                <StatCard testid="ps-no-contact" value={counts.no_contact_found || 0} label="No Contact Found" tone="red" />
              </div>
            </div>

            {/* Quick actions */}
            <div>
              <div className="text-[11px] uppercase tracking-wider text-white/40 mb-2">
                Quick actions
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={runBulkEnrich}
                  disabled={busy === 'enrich-all'}
                  data-testid="ps-action-enrich"
                  className="flex items-center gap-2 px-3 py-2 border border-white/15 text-sm hover:border-[#00D4FF]/60 hover:text-[#00D4FF] disabled:opacity-50"
                >
                  {busy === 'enrich-all'
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <RefreshCw className="w-4 h-4" />}
                  Enrich Missing Contacts
                </button>
                <button
                  onClick={runBulkVerify}
                  disabled={busy === 'verify-all'}
                  data-testid="ps-action-verify"
                  className="flex items-center gap-2 px-3 py-2 border border-white/15 text-sm hover:border-[#00D4FF]/60 hover:text-[#00D4FF] disabled:opacity-50"
                >
                  {busy === 'verify-all'
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <ShieldCheck className="w-4 h-4" />}
                  Verify Supplier Emails
                </button>
                <button
                  onClick={() => setShowBlocked(true)}
                  data-testid="ps-action-blocked"
                  className="flex items-center gap-2 px-3 py-2 border border-red-400/30 text-red-200 text-sm hover:bg-red-400/10"
                >
                  <XCircle className="w-4 h-4" />
                  View Blocked Suppliers ({blockedRows.length})
                </button>
                <button
                  onClick={() => willSendCount === 0
                    ? toast.error('No suppliers eligible to send')
                    : setConfirmOpen(true)}
                  data-testid="ps-action-send-verified"
                  className="flex items-center gap-2 px-3 py-2 bg-[#00D4FF] text-black font-semibold text-sm hover:bg-[#00D4FF]/90 disabled:opacity-50"
                >
                  <Send className="w-4 h-4" />
                  Send To Verified Only ({willSendCount})
                </button>
              </div>

              <label className="flex items-center gap-2 mt-3 text-sm text-white/70 cursor-pointer" data-testid="ps-allow-risky-wrap">
                <input
                  type="checkbox"
                  checked={allowRisky}
                  onChange={(e) => setAllowRisky(e.target.checked)}
                  data-testid="ps-allow-risky"
                />
                Include Risky / Unknown Emails (will also try unverified addresses with MX records)
              </label>
            </div>

            {/* Filters + breakdown */}
            <div>
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div className="text-[11px] uppercase tracking-wider text-white/40">
                  Supplier breakdown
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="relative">
                    <Search className="w-4 h-4 text-white/30 absolute left-2 top-1/2 -translate-y-1/2" />
                    <input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search…"
                      data-testid="ps-search"
                      className="pl-8 pr-3 py-1.5 bg-white/5 border border-white/10 text-sm w-48 outline-none focus:border-[#00D4FF]/50"
                    />
                  </div>
                  {[
                    { key: 'all', label: `All (${breakdown.length})` },
                    { key: 'ready', label: `Ready (${breakdown.filter(b => b.result === 'ready').length})` },
                    { key: 'needs_attention', label: `Attention (${breakdown.filter(b => b.result === 'needs_attention').length})` },
                    { key: 'blocked', label: `Blocked (${breakdown.filter(b => b.result === 'blocked').length})` },
                  ].map((f) => (
                    <button
                      key={f.key}
                      onClick={() => setFilter(f.key)}
                      data-testid={`ps-filter-${f.key}`}
                      className={`px-2.5 py-1.5 border text-xs ${
                        filter === f.key
                          ? 'border-[#00D4FF] text-[#00D4FF]'
                          : 'border-white/10 text-white/60 hover:text-white'
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="border border-white/10 overflow-x-auto max-h-[400px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-white/5 text-white/60 text-xs uppercase tracking-wider sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2">Company</th>
                      <th className="text-left px-3 py-2">Email</th>
                      <th className="text-left px-3 py-2">Email Status</th>
                      <th className="text-left px-3 py-2">Enrichment</th>
                      <th className="text-left px-3 py-2">Result</th>
                      <th className="text-right px-3 py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.length === 0 && (
                      <tr><td colSpan="6" className="px-3 py-8 text-center text-white/40">No suppliers match this filter.</td></tr>
                    )}
                    {filteredRows.map((b) => {
                      const evColor = EV_COLOR[b.email_verification_status] || 'text-white/60';
                      return (
                        <tr key={b.supplier_id} className="border-t border-white/5" data-testid={`ps-row-${b.supplier_id}`}>
                          <td className="px-3 py-2 font-medium">{b.company_name}</td>
                          <td className="px-3 py-2 text-white/60 text-xs">{b.email || <span className="text-red-300">—</span>}</td>
                          <td className={`px-3 py-2 text-xs ${evColor}`}>
                            {EV_LABEL[b.email_verification_status] || b.email_verification_status}
                          </td>
                          <td className="px-3 py-2 text-xs text-white/60">
                            {ENRICH_LABEL[b.enrichment_status] || b.enrichment_status}
                          </td>
                          <td className="px-3 py-2">
                            <span className={`inline-block px-2 py-0.5 border text-[11px] ${RESULT_BADGE[b.result]}`}>
                              {b.result === 'ready' ? 'Ready' : b.result === 'needs_attention' ? 'Attention' : 'Blocked'}
                            </span>
                            <div className="text-[10px] text-white/40 mt-1">{b.reason}</div>
                          </td>
                          <td className="px-3 py-2 text-right whitespace-nowrap">
                            <button
                              onClick={() => runEnrichOne(b.supplier_id)}
                              disabled={busy === `enrich-${b.supplier_id}`}
                              data-testid={`ps-enrich-${b.supplier_id}`}
                              className="px-2 py-1 text-[11px] border border-white/10 text-white/60 hover:text-[#00D4FF] hover:border-[#00D4FF]/40 disabled:opacity-50 mr-1"
                              title="Enrich supplier"
                            >
                              {busy === `enrich-${b.supplier_id}` ? '…' : 'Enrich'}
                            </button>
                            <button
                              onClick={() => runVerifyOne(b.supplier_id)}
                              disabled={busy === `verify-${b.supplier_id}` || !b.email}
                              data-testid={`ps-verify-${b.supplier_id}`}
                              className="px-2 py-1 text-[11px] border border-white/10 text-white/60 hover:text-emerald-300 hover:border-emerald-400/40 disabled:opacity-30"
                              title="Verify email"
                            >
                              {busy === `verify-${b.supplier_id}` ? '…' : 'Verify'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Footer send bar */}
        {data && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-white/10 sticky bottom-0 bg-[#0A0A0A]">
            <div className="text-sm text-white/70">
              {willSendCount > 0
                ? <><span className="text-emerald-300 font-semibold">{willSendCount}</span> ready to send · <span className="text-red-300">{blockedCount}</span> will be skipped</>
                : <span className="text-red-300">No suppliers eligible to send</span>
              }
              {allowRisky && <span className="ml-2 text-amber-300 text-xs">· Risky / Unknown included</span>}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={onClose} className="px-4 py-2 text-sm text-white/60 hover:text-white" data-testid="ps-cancel">
                Close
              </button>
              <button
                onClick={() => setConfirmOpen(true)}
                disabled={willSendCount === 0}
                data-testid="ps-send-btn"
                className="flex items-center gap-2 px-4 py-2 bg-[#00D4FF] text-black font-semibold text-sm disabled:opacity-40 hover:bg-[#00D4FF]/90"
              >
                <Send className="w-4 h-4" />
                Send RFQ ({willSendCount})
              </button>
            </div>
          </div>
        )}

        {/* View Blocked modal */}
        {showBlocked && (
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4"
            onClick={() => setShowBlocked(false)}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-3xl bg-[#0A0A0A] border border-red-400/40 max-h-[80vh] overflow-y-auto"
              data-testid="ps-blocked-modal"
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 sticky top-0 bg-[#0A0A0A]">
                <div>
                  <div className="text-xs uppercase tracking-wider text-red-300">Blocked / At-Risk Suppliers</div>
                  <h3 className="text-lg font-semibold">{blockedRows.length} supplier(s) won&apos;t receive this RFQ</h3>
                </div>
                <button onClick={() => setShowBlocked(false)} className="text-white/40 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-5">
                {blockedRows.length === 0 ? (
                  <div className="text-center py-8 text-emerald-300 flex flex-col items-center gap-2">
                    <CheckCircle2 className="w-6 h-6" />
                    Nothing blocked — every supplier will receive the RFQ.
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase text-white/40">
                      <tr>
                        <th className="text-left py-2">Company</th>
                        <th className="text-left py-2">Email</th>
                        <th className="text-left py-2">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {blockedRows.map((b) => (
                        <tr key={b.supplier_id} className="border-t border-white/5" data-testid={`ps-blocked-row-${b.supplier_id}`}>
                          <td className="py-2">{b.company_name}</td>
                          <td className="py-2 text-white/60 text-xs">{b.email || '—'}</td>
                          <td className="py-2 text-red-200/80 text-xs">{b.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Confirm send modal */}
        {confirmOpen && (
          <div
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/85 p-4"
            onClick={() => !sending && setConfirmOpen(false)}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md bg-[#0A0A0A] border border-[#00D4FF]/40"
              data-testid="ps-confirm-modal"
            >
              <div className="px-5 py-4 border-b border-white/10 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-300" />
                <h3 className="text-lg font-semibold">Confirm RFQ Send</h3>
              </div>
              <div className="px-5 py-4 space-y-3 text-sm">
                <p className="text-white/80">
                  You are about to send RFQs to <span className="text-emerald-300 font-semibold">{willSendCount}</span> supplier(s).
                </p>
                {blockedCount > 0 && (
                  <p className="text-white/80">
                    <span className="text-red-300 font-semibold">{blockedCount}</span> supplier(s) will be skipped (blocked / unverified).
                  </p>
                )}
                {allowRisky && (
                  <p className="text-amber-200 text-xs border border-amber-400/30 bg-amber-400/5 px-3 py-2">
                    Risky / Unknown emails are included. Bounce rate may rise.
                  </p>
                )}
                <p className="text-white/50 text-xs">Continue?</p>
              </div>
              <div className="px-5 py-4 border-t border-white/10 flex justify-end gap-2">
                <button
                  onClick={() => setConfirmOpen(false)}
                  disabled={sending}
                  data-testid="ps-confirm-cancel"
                  className="px-4 py-2 text-sm text-white/60 hover:text-white disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={doSend}
                  disabled={sending}
                  data-testid="ps-confirm-send"
                  className="flex items-center gap-2 px-4 py-2 bg-[#00D4FF] text-black font-semibold text-sm hover:bg-[#00D4FF]/90 disabled:opacity-50"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {sending ? 'Sending…' : 'Confirm Send'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RfqPreSendPanel;
